/**
 * Secure Clerk Configuration Loader
 * 
 * Loads Clerk configuration from server-side API endpoint
 * that reads from secure .env file outside web root
 */

let clerkConfig = null;
let configPromise = null;

const loadClerkConfig = async () => {
  // Return cached config if available
  if (clerkConfig) {
    return clerkConfig;
  }

  // Return existing promise if already loading
  if (configPromise) {
    return configPromise;
  }

  configPromise = (async () => {
    try {
      // Try to fetch from the API endpoint
      const response = await fetch('/api/get-clerk-config.php');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const config = await response.json();
      
      if (config.error) {
        console.warn('Clerk configuration error:', config.error);
        clerkConfig = {
          available: false,
          publishableKey: null,
          error: config.error
        };
      } else {
        console.log('Clerk configuration loaded successfully');
        clerkConfig = {
          available: config.available,
          publishableKey: config.publishableKey,
          error: null
        };
      }
      
      return clerkConfig;
    } catch (error) {
      console.warn('Failed to load Clerk configuration:', error.message);
      
      // Fallback: try environment variables (for development)
      const fallbackKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
      
      clerkConfig = {
        available: !!fallbackKey,
        publishableKey: fallbackKey || null,
        error: fallbackKey ? null : 'No Clerk configuration available'
      };
      
      return clerkConfig;
    } finally {
      configPromise = null;
    }
  })();

  return configPromise;
};

// Synchronous getter for when config is already loaded
const getClerkConfig = () => {
  return clerkConfig;
};

// Check if Clerk is available
const isClerkAvailable = () => {
  return clerkConfig?.available === true && clerkConfig?.publishableKey;
};

// Get publishable key if available
const getClerkPublishableKey = () => {
  return clerkConfig?.publishableKey || '';
};

export {
  loadClerkConfig,
  getClerkConfig,
  isClerkAvailable,
  getClerkPublishableKey
}; 